import {
  ClipboardIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  QuestionMarkCircleIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline';
import Dropdown, { Option } from 'components/commercetools-ui/atoms/dropdown';
import React, { FC, useEffect, useState } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet, largeDesktop, desktop, mediumDesktop, semiLargeDesktop, extraLargeDesktop } from 'helpers/utils/screensizes';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { useParams } from 'next/navigation';


interface SidebarPDPProps {
  bundleData?: any[] | undefined;
  sparePartsData?: any[] | undefined;
}

const SidebarPDP: FC<SidebarPDPProps> = ({bundleData, sparePartsData}) =>  {
  const [activeSection, setActiveSection] = useState(0);
  const [isTablet] = useMediaQuery(tablet);
  const [isLargeDesktop] = useMediaQuery(semiLargeDesktop);
  const [isHugeLargeDesktop] = useMediaQuery(extraLargeDesktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [isDesktop] = useMediaQuery(desktop);
  const { isTouchDevice } = useTouchDevice();
  const [distanceToTop, setDistanceToTop] = useState(1000);
  const [sidebarHeight, setSidebarHeight] = useState(0);
  const width = useWindowWidth();
  const { locale } = useParams();
  
  const sidebarSections: { name: string; icon: any; value: string; }[] | undefined = [];
  sidebarSections.push({ name: 'Product overview', icon: <InformationCircleIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'product-overview-scroll' });
  sidebarSections.push({ name: 'Technical info', icon: <ClipboardIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'specifier-info' });
  bundleData && bundleData.length > 0 && sidebarSections.push({ 
    name: 'Components', 
    icon: (
      <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7 7H10.8667C11.8922 7 12.8757 7.40738 13.6008 8.13252C14.326 8.85766 14.7333 9.84116 14.7333 10.8667V45.0222C14.7333 46.0477 15.1407 47.0312 15.8659 47.7564C16.591 48.4815 17.5745 48.8889 18.6 48.8889H65M41.8 48.8889H22.4667V31.0249C22.4667 30.2934 22.7573 29.5918 23.2745 29.0745C23.7918 28.5573 24.4934 28.2667 25.2249 28.2667H39.0418C39.404 28.2667 39.7627 28.338 40.0973 28.4766C40.4319 28.6152 40.736 28.8184 40.9921 29.0745C41.2483 29.3307 41.4514 29.6347 41.59 29.9694C41.7287 30.304 41.8 30.6627 41.8 31.0249V48.8889ZM41.8 48.8889L61.1333 48.8889V34.3502C61.0433 33.6985 60.7075 33.1059 60.1947 32.6937C59.6819 32.2816 59.0309 32.0811 58.3751 32.1333H44.5582C43.9024 32.0811 43.2514 32.2816 42.7386 32.6937C42.2258 33.1059 41.89 33.6985 41.8 34.3502V48.8889ZM32.1333 19.8889V17.3111C32.1333 16.2856 32.5407 15.3021 33.2659 14.577C33.991 13.8518 34.9745 13.4444 36 13.4444H47.6C48.6255 13.4444 49.609 13.8518 50.3341 14.577C51.0593 15.3021 51.4667 16.2856 51.4667 17.3111V24.4M10.8667 61.1333C10.8667 62.1588 11.274 63.1423 11.9992 63.8675C12.7243 64.5926 13.7078 65 14.7333 65C15.7588 65 16.7423 64.5926 17.4675 63.8675C18.1926 63.1423 18.6 62.1588 18.6 61.1333C18.6 60.1078 18.1926 59.1243 17.4675 58.3992C16.7423 57.674 15.7588 57.2667 14.7333 57.2667C13.7078 57.2667 12.7243 57.674 11.9992 58.3992C11.274 59.1243 10.8667 60.1078 10.8667 61.1333ZM53.4 61.1333C53.4 62.1588 53.8074 63.1423 54.5325 63.8675C55.2577 64.5926 56.2412 65 57.2667 65C58.2922 65 59.2757 64.5926 60.0008 63.8675C60.726 63.1423 61.1333 62.1588 61.1333 61.1333C61.1333 60.1078 60.726 59.1243 60.0008 58.3992C59.2757 57.674 58.2922 57.2667 57.2667 57.2667C56.2412 57.2667 55.2577 57.674 54.5325 58.3992C53.8074 59.1243 53.4 60.1078 53.4 61.1333Z"
          stroke="#666666"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    value: 'components-section'});  
    sparePartsData && sparePartsData.length > 0 && sidebarSections.push({ 
    name: 'Spare Parts', 
    icon: (
      <svg width="32" height="32" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.52442 7.52442C5.20187 12.4949 11.0565 25.3968 16.0116 30.3518C19.8161 34.4489 26.2546 34.4489 30.3519 30.3518C34.4492 26.5472 34.1565 20.1087 30.3519 16.0115M7.52442 7.52442L16.0116 16.0115C20.1088 11.9143 26.5473 11.9143 30.3519 16.0115M7.52442 7.52442C12.4949 5.20188 25.3968 11.0565 30.3519 16.0115M30.3564 30.3565L35.1537 35.1536M23.8241 33.3993L29.9446 43.6384M33.4001 23.8235L43.6392 29.944M23.1816 23.7192C22.8849 23.7192 22.6444 23.4786 22.6444 23.1819C22.6444 22.8852 22.8849 22.6447 23.1816 22.6447C23.4783 22.6447 23.7189 22.8851 23.7189 23.1818C23.7189 23.4785 23.4783 23.7192 23.1816 23.7192ZM46.219 55.5085C47.8309 59.0006 46.219 63.0296 42.7271 64.3728C39.2352 65.9844 35.4746 64.3728 33.8627 60.8809L28.8888 50.0116C27.6374 47.2774 28.874 44.0482 31.6317 42.8494L34.1327 41.7619C36.817 40.5949 39.9417 41.7917 41.16 44.4532L46.219 55.5085ZM55.5086 46.2192C59.0005 47.8308 63.0298 46.2192 64.3727 42.727C65.9846 39.2351 64.3727 35.4745 60.8808 33.8629L50.0117 28.8888C47.2772 27.6375 44.0483 28.8741 42.8492 31.6317L41.762 34.1327C40.5947 36.8172 41.7915 39.9419 44.453 41.1599L55.5086 46.2192Z"
          stroke="#666666"
          strokeWidth="4"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    value: 'spare-parts-section'});
    {(locale === 'au') &&
      sidebarSections.push({ name: 'Advice', icon: <MegaphoneIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'advice-section' });
    }
  sidebarSections.push({ name: 'Related Products', icon: <Square2StackIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'related-products'});
  //console.log('sidebarSections - ',sidebarSections);
  const scrollTo = (ele: HTMLElement) => {
    const position = ele.getBoundingClientRect();
    window.scrollTo({
      top: position.top + window.scrollY - (isHugeLargeDesktop? 115 : isLargeDesktop ? 90 : isMediumDesktop ? 50 : isTablet? 75: 100),
      behavior: 'smooth',
    });
  };
  const scrollToDesktop = (id: string) => {
    const ele = document.getElementById(id);
    if (ele) {
      scrollTo(ele);
    }
  };
  const scrollToMobile = (option: Option) => {
    const ele = document.getElementById(option.value.toString());
    if (ele) {
      scrollTo(ele);
    }
  };


  const handleClick = (index: React.SetStateAction<number>, id: string) => {
    scrollToDesktop(id);
  };

  useEffect(() => {
    const handleScroll = () => {
      if(!sidebarHeight && document && document.getElementById('related-products-title')){
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const rect = document.getElementById('related-products-title')?.getBoundingClientRect();
        rect && setSidebarHeight(rect.top + scrollTop);
      }
    
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setActiveSection(sidebarSections.length-1);
      } else {
        const screenHeightFactor = window.innerHeight > window.innerWidth ? 5 : 3;
        sidebarSections.forEach((section, index)=>{
          const element = document.getElementById(section.value);  
          if (element) {
            const rect = element.getBoundingClientRect();
            const isInView = rect.top >= 0 && rect.top <= window.innerHeight/screenHeightFactor;
            if (isInView) {
              setActiveSection(index);
            }
          }

        });
      }

    };


    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, 
  
  [isMediumDesktop, isTablet, isDesktop]);

  return (
    <>
      {width !== null && width < 1024 ? (
        <div
        //   style={{ marginTop: `${distanceToTop / 20 - 30}rem` }} 
          className="absolute w-full left-full z-10 mb-48 w-screen -translate-x-full px-24 lg:grid lg:grid-cols-12"
        >
          <Dropdown
            selectOptions={sidebarSections}
            selectOnChange={scrollToMobile}
            selectDropdownClassNames="p-16 w-full"
          ></Dropdown>
        </div>
      ) : (
        <div
          style={{ height: `${sidebarHeight / 15 - 6}rem` }}
          className="relative mt-[5rem] "
        >
          <div className="h-full flex-col text-18 font-semibold leading-6 ">
            <div className="sticky left-0  top-[9rem] 2xl:top-[12rem] !z-[40] mb-40 w-fit">
              {sidebarSections.map((section, index) => (
                <div
                  className={`flex items-center gap-12 py-18 2xl:py-24 pl-18 hover:cursor-pointer 2xl:pl-32 ${
                    index === activeSection ? 'border-l-4 border-[#090909]' : 'border-l-4 border-[#F3EDE9]'
                  }`}
                  key={index}
                  onClick={() => handleClick(index, section.value)}
                >
                  {section.icon}
                  <span className="text-16 font-semibold leading-6 text-primary-black lg:text-14 3xl:text-18">
                    {section.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SidebarPDP;
